/**
 * This file contains the interceptors for the Bookingkit API
 * It is used to handle the responses and errors from the API, generating the correct error classes
 * and logging the errors.
 *
 */
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { CanceledRequestError } from '@/Factories/BookingkitApi/Errors/CanceledRequestError';
import { ResponseMissingStatusCodeError } from '@/Factories/BookingkitApi/Errors/ResponseMissingStatusCodeError';
import { ApiV4ClientError } from '@/Factories/BookingkitApi/Errors/ApiV4ClientError';
import { ApiV4ServerError } from '@/Factories/BookingkitApi/Errors/ApiV4ServerError';
import { LoggerServiceInterface } from '@/models/Services/LoggerServiceInterface';
import { ValidationError } from '@/Factories/BookingkitApi/Errors/ValidationError';
import { IgnorableNetworkError } from '@/Factories/BookingkitApi/Errors/IgnorableNetworkError';
import {ApiV4AxiosError} from "@/Factories/BookingkitApi/Errors/ApiV4AxiosError";
import {UnAuthenticatedError} from "@/Factories/BookingkitApi/Errors/UnAuthenticatedError";
import {RequestAbortedError} from "@/Factories/BookingkitApi/Errors/RequestAbortedError";
import {UnAuthorisedError} from "@/Factories/BookingkitApi/Errors/UnAuthorisedError";
import {ApiNetworkError} from "@/Factories/BookingkitApi/Errors/ApiNetworkError";

export const onFullFilled = (response: AxiosResponse) => response;

const NOT_REPORTED_ERRORS = [
    CanceledRequestError,
    ValidationError,
    ApiV4ClientError,
    IgnorableNetworkError,
    ApiV4ServerError,
];

const STRIPE_OBJECT_LOCK_ERROR_MESSAGE_PARTS = [
    'This object cannot be accessed right now',
    'another API request or Stripe process is currently accessing it',
];

const STRIPE_OBJECT_LOCK_MAX_RETRIES = 2;
const STRIPE_OBJECT_LOCK_RETRY_DELAY_MS = 1000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getStripeObjectLockMessage = (error?: any) => (
    error?.response?.data?.error_message
    || error?.response?.data?.message
    || error?.message
    || ''
);

const isStripeObjectLockError = (error?: any) => (
    STRIPE_OBJECT_LOCK_ERROR_MESSAGE_PARTS
        .some((part) => getStripeObjectLockMessage(error).includes(part))
);

const retryStripeObjectLock = async (error?: any): Promise<any | null> => {
    if (!isStripeObjectLockError(error)) {
        return null;
    }

    const config = error?.config as (AxiosRequestConfig & { __stripeObjectLockRetryCount?: number }) | undefined;
    if (!config) {
        return null;
    }

    const retryCount = config.__stripeObjectLockRetryCount ?? 0;
    if (retryCount >= STRIPE_OBJECT_LOCK_MAX_RETRIES) {
        return null;
    }

    config.__stripeObjectLockRetryCount = retryCount + 1;
    await sleep(STRIPE_OBJECT_LOCK_RETRY_DELAY_MS);
    return axios(config);
};

const getErrorFromCode = (error: ApiV4AxiosError) => {
    const errorMessage = error.response?.data?.error_message as string | undefined;
    if (!error.response?.status) {
        return ApiV4ServerError.fromAxiosError(error);
    }
    if (error.response.status === 401) {
        return UnAuthenticatedError.fromUnAuthenticatedRequest();
    }
    if (error.response.status === 403) {
        return UnAuthorisedError.fromUnAuthorisedRequest();
    }
    if (error.response.status === 422) {
        return ValidationError.fromAxiosError(error);
    }
    if (error.response.status >= 400 && error.response?.status < 500) {
        return ApiV4ClientError.fromAxiosError(error);
    }
    return ApiV4ServerError.fromAxiosError(error);
};

const parseError = (error?: any): Error => {
    if (error?.message === 'Network Error' && error?.response === undefined) {
        let isOnline = true;

        try {
            // Navigator can tell us if is offline
            isOnline = window.navigator.onLine;
        } catch {
            // but can rarely trow in some browser,
            // so better to catch the error straight away
            return ApiNetworkError.fromApiNetworkError();
        }
        if (isOnline) {
            // If the client is online but the request fails before reaching the EP,
            // It must be blocked from some extension / dev tool / network problem
            return ApiNetworkError.fromApiNetworkError();
        }

        // if we reach here, means we are offline
        return ResponseMissingStatusCodeError.fromOfflineClient(error);
    }

    if (error?.message === 'canceled') {
        return CanceledRequestError.fromCanceledRequest();
    }


    if (error?.message === 'Request aborted') {
        return RequestAbortedError.fromRequestAbortedRequest(error);
    }

    // This is the case when the request reached the server
    // but the server responded with an error
    if (error?.response?.status) {
        return getErrorFromCode(error);
    }

    // We can log only what we want to log
    if (error instanceof Error) {
        return error;
    }

    return new Error('Unknown error');
};

const shouldReport = (error: Error) => !NOT_REPORTED_ERRORS.some((errorType) => error instanceof errorType);
const logError = (logger: LoggerServiceInterface, error: Error) => {
    if (shouldReport(error)) {
        logger.captureException(error);
    }
};

export const createOnRejected = (logger: LoggerServiceInterface) => async (error?: any): Promise<any> => {
    const retryResult = await retryStripeObjectLock(error);
    if (retryResult !== null) {
        return retryResult;
    }

    const parsedError = parseError(error);
    logError(logger, parsedError);
    return Promise.reject(parsedError);
};
